import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { searchScopeWheelTargets } from '../../api/scope_wheel_heroes';
import { kindLabel } from '../../helpers/scope_wheel_helpers';
import './WheelTargetPicker.scss';

const WheelTargetPicker = ({ kind, label, selectedLabel, onSelect, onClear }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const revision = useRef(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const search = useMemo(
    () =>
      _.debounce(async (kindToSearch, value, requestRevision) => {
        try {
          const found = await searchScopeWheelTargets(kindToSearch, value.trim());
          if (revision.current === requestRevision) setResults(found);
        } catch {
          if (revision.current === requestRevision) { setResults([]); setError('Search is unavailable. Try again.'); }
        } finally {
          if (revision.current === requestRevision) setIsLoading(false);
        }
      }, 250),
    []
  );

  useEffect(() => () => { revision.current++; search.cancel(); }, [search]);

  const onChange = event => {
    const value = event.target.value;
    setQuery(value);
    setIsLoading(value.trim().length >= 2);
    setError('');
    setResults([]);
    revision.current++;
    search.cancel();
    if (value.trim().length >= 2) search(kind, value, revision.current);
  };

  if (selectedLabel) {
    return (
      <div className='wheel-target-picker'>
        <div className='wheel-target-picker-label'>{label || `Shown on ${kindLabel(kind).toLowerCase()}`}</div>
        <div className='wheel-target-picker-selected'>
          <span>{selectedLabel}</span>
          <button type='button' onClick={onClear}>
            Clear
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='wheel-target-picker'>
      <div className='wheel-target-picker-label'>{label || `Show on a ${kindLabel(kind).toLowerCase()}`}</div>
      <input value={query} onChange={onChange} placeholder={`Search ${kindLabel(kind).toLowerCase()}s`} />
      {(isLoading || query.trim()) && (
        <div className='wheel-target-picker-results'>
          {isLoading && !results.length && <div className='wheel-target-picker-empty'>Looking…</div>}
          {!isLoading && !results.length && <div className='wheel-target-picker-empty'>{query.trim().length < 2 ? 'Keep typing — enter at least two characters.' : error || 'Nothing matches.'}</div>}
          {results.map(result => (
            <button
              key={result.id}
              type='button'
              onClick={() => {
                onSelect(result);
                setQuery('');
                setResults([]);
              }}
            >
              <span>{result.label}</span>
              {result.extra && <em>{result.extra}</em>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

WheelTargetPicker.propTypes = {
  kind: PropTypes.string.isRequired,
  label: PropTypes.string,
  selectedLabel: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
};

export default WheelTargetPicker;
